import React, { useState, useEffect, createContext, lazy, Suspense } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "./firebase";
import HomePage from "./pages/HomePage";
import MonthPage from "./pages/MonthPage";
import DayPage from "./pages/DayPage";
import TradesPage from "./pages/TradesPage";
import ContextPage from "./pages/ContextPage";
import SummaryPage from "./pages/SummaryPage";
import Layout from "./components/Layout";
import LandingPage from "./pages/LandingPage";
import NotebookPage from "./pages/NotebookPage";
import EditTradePage from "./pages/EditTradePage";
import QuickAddPage from "./pages/QuickAddPage";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ToastProvider, ConfirmProvider, PageLoader } from "./components/ui";
import { LanguageProvider } from "./i18n/LanguageContext";
import LanguageSetupModal from "./i18n/LanguageSetupModal";
import RouteTitle from "./components/RouteTitle";
import OfflineBanner from "./components/OfflineBanner";
import AddAccountModal from "./components/AddAccountModal";
import WeeklyReviewAutomation from "./components/WeeklyReviewAutomation";
import ICTEmailToast from "./components/ICTEmailToast";
import BandzEliteToast from "./components/BandzEliteToast";
import {
  buildAccountRecord,
  getEvaluationProgress,
  splitAccountsByArchive,
} from "./utils/accountLifecycle";

// Less-used routes load on demand to keep the first download small.
const VisualizationPage = lazy(() => import("./pages/VisualizationPage"));
const NotebookGraphPage = lazy(() => import("./pages/NotebookGraphPage"));
const EditAccountPage = lazy(() => import("./pages/EditAccountPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AdminPage = lazy(() => import("./pages/AdminPage"));
const TestDataPage = lazy(() => import("./pages/TestDataPage"));
const AccountActivityPage = lazy(() => import("./pages/AccountActivityPage"));
const FeedbackPage = lazy(() => import("./pages/FeedbackPage"));
const SocialsPage = lazy(() => import("./pages/SocialsPage"));
const TerminalPage = lazy(() => import("./pages/TerminalPage"));
const BandzBotPage = lazy(() => import("./pages/BandzBotPage"));
const ChecklistPage = lazy(() => import("./pages/ChecklistPage"));
const ThemeStudioPage = lazy(() => import("./pages/ThemeStudioPage"));
const FeaturePreviewPage = lazy(() => import("./pages/FeaturePreviewPage"));
const CsvImportPage = lazy(() => import("./pages/CsvImportPage"));
const BrokerConnectionsPage = lazy(() => import("./pages/BrokerConnectionsPage"));
const WebullCallbackPage = lazy(() => import("./pages/WebullCallbackPage"));

export const UserContext = createContext();

function App() {
  const [currentUser, setCurrentUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [accounts, setAccounts] = useState([]);
  const [archivedAccounts, setArchivedAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [accountToDelete, setAccountToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [dataRefreshTrigger, setDataRefreshTrigger] = useState(0);
  const [showAddAccountModal, setShowAddAccountModal] = useState(false);
  // False until the first accounts read finishes, so the sidebar doesn't flash "Add Account".
  const [accountsLoaded, setAccountsLoaded] = useState(false);
  const [showICTEmail, setShowICTEmail] = useState(false);
  const [showBandzEmail, setShowBandzEmail] = useState(false);
  const [easterEggs, setEasterEggs] = useState({
    ictEmail: true,
    bandzEmail: true,
    rocketShip: true
  });

  // Load easter egg preferences
  useEffect(() => {
    if (!currentUser) return;
    
    const loadEasterEggs = async () => {
      try {
        const { db } = await import('./firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          if (data.easterEggs) {
            setEasterEggs({
              ictEmail: data.easterEggs.ictEmail !== false,
              bandzEmail: data.easterEggs.bandzEmail !== false,
              rocketShip: data.easterEggs.rocketShip !== false
            });
          }
        }
      } catch (error) {
        console.error('Error loading easter egg preferences:', error);
      }
    };
    
    loadEasterEggs();
  }, [currentUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setAuthReady(true);
    });
    return () => unsubscribe();
  }, []);

  // Create user profile document on first login and fetch accounts
  useEffect(() => {
    if (!currentUser) return;
    async function initializeUser() {
      const { db } = await import('./firebase');
      const { doc, getDoc, setDoc, collection, getDocs } = await import('firebase/firestore');
      
      // Create or update user profile document
      try {
        const userProfileRef = doc(db, 'users', currentUser.uid);
        const userProfileSnap = await getDoc(userProfileRef);
        
        if (!userProfileSnap.exists()) {
          // First time user - create profile
          await setDoc(userProfileRef, {
            email: currentUser.email || '',
            displayName: currentUser.displayName || '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          });
        } else {
          // Update existing profile with latest auth data
          await setDoc(userProfileRef, {
            email: currentUser.email || userProfileSnap.data().email || '',
            displayName: currentUser.displayName || userProfileSnap.data().displayName || '',
            updatedAt: new Date().toISOString()
          }, { merge: true });
        }
      } catch (error) {
        console.error('Error creating/updating user profile:', error);
      }
      
      // Fetch accounts
      const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
      const snap = await getDocs(accountsCol);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const { active: activeAccounts, archived } = splitAccountsByArchive(data);
      
      // Load selected account preference
      try {
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        const savedAccountId = prefsSnap.exists() ? prefsSnap.data().selectedAccountId : null;
        
        // If no accounts exist, show the Add Account modal
        if (activeAccounts.length === 0) {
          setAccounts([]);
          setArchivedAccounts(archived);
          setSelectedAccount(null);
          setShowAddAccountModal(archived.length === 0);
        } else {
          setAccounts(activeAccounts);
          setArchivedAccounts(archived);
          // Try to restore saved account, otherwise use first account
          if (savedAccountId) {
            const savedAccount = activeAccounts.find(acc => acc.id === savedAccountId);
            if (savedAccount) {
              setSelectedAccount(savedAccount);
            } else {
              setSelectedAccount(activeAccounts[0]);
              // Update preference if saved account no longer exists
              await setDoc(prefsRef, { selectedAccountId: activeAccounts[0].id }, { merge: true });
            }
          } else {
            setSelectedAccount(activeAccounts[0]);
            // Save first account as preference
            await setDoc(prefsRef, { selectedAccountId: activeAccounts[0].id }, { merge: true });
          }
        }
      } catch (error) {
        console.error('Error loading accounts or preferences:', error);
        // Fallback: just set accounts without preference loading
        if (activeAccounts.length > 0) {
          setAccounts(activeAccounts);
          setArchivedAccounts(archived);
          if (!selectedAccount) {
            setSelectedAccount(activeAccounts[0]);
          }
        } else {
          setAccounts([]);
          setArchivedAccounts(archived);
          setSelectedAccount(null);
          setShowAddAccountModal(archived.length === 0);
        }
      }
    }
    setAccountsLoaded(false);
    initializeUser()
      .catch((error) => console.error('Error initializing user:', error?.message))
      .finally(() => setAccountsLoaded(true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Function to refresh accounts from Firestore
  const refreshAccounts = async () => {
    if (!currentUser) return;
    try {
      const { db } = await import('./firebase');
      const { collection, getDocs, doc, setDoc } = await import('firebase/firestore');
      const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
      const snap = await getDocs(accountsCol);
      const data = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const { active: activeAccounts, archived } = splitAccountsByArchive(data);
      setAccounts(activeAccounts);
      setArchivedAccounts(archived);
      
      // If no active accounts exist, keep archived records accessible for restoration.
      if (activeAccounts.length === 0) {
        setSelectedAccount(null);
        setShowAddAccountModal(archived.length === 0);
        return;
      }
      
      const refreshedSelectedAccount = selectedAccount
        ? activeAccounts.find(acc => acc.id === selectedAccount.id)
        : null;

      if (refreshedSelectedAccount) {
        setSelectedAccount(refreshedSelectedAccount);
      } else {
        // If the selected account was archived, select the first active account.
        if (activeAccounts.length > 0) {
          setSelectedAccount(activeAccounts[0]);
          const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
          await setDoc(prefsRef, { selectedAccountId: activeAccounts[0].id }, { merge: true });
        } else {
          setSelectedAccount(null);
        }
      }
    } catch (error) {
      console.error('Error refreshing accounts:', error);
    }
  };

  // Function to refresh easter eggs from Firestore
  const refreshEasterEggs = async () => {
    if (!currentUser) return;
    try {
      const { db } = await import('./firebase');
      const { doc, getDoc } = await import('firebase/firestore');
      const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
      const prefsSnap = await getDoc(prefsRef);
      
      if (prefsSnap.exists()) {
        const data = prefsSnap.data();
        if (data.easterEggs) {
          setEasterEggs({
            ictEmail: data.easterEggs.ictEmail !== false,
            bandzEmail: data.easterEggs.bandzEmail !== false,
            rocketShip: data.easterEggs.rocketShip !== false
          });
        }
      }
    } catch (error) {
      console.error('Error refreshing easter egg preferences:', error);
    }
  };

  // Handle account creation with automatic deposit entry
  const handleCreateAccount = async (accountData) => {
    if (!currentUser) throw new Error('User not authenticated');

    const { balance } = accountData;
    const { db } = await import('./firebase');
    const { collection, addDoc, doc, setDoc, getDocs } = await import('firebase/firestore');

    // Create account document
    const accountsCol = collection(db, 'users', currentUser.uid, 'accounts');
    const accountRecord = buildAccountRecord(accountData);
    const docRef = await addDoc(accountsCol, accountRecord);

    // Automatically create deposit entry
    const now = new Date();
    const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', docRef.id, 'entries');
    await addDoc(entriesCol, {
      pnl: Number(balance), // Store deposit amount in pnl field for consistency
      notes: 'Initial deposit',
      created: now.toISOString(),
      isDeposit: true,
      accountBalance: Number(balance), // Initial balance equals deposit amount
      year: now.getFullYear().toString(),
      month: (now.getMonth() + 1).toString(),
      day: now.getDate().toString(),
    });

    // Refresh accounts from Firestore
    await refreshAccounts();
    
    // Fetch accounts directly to get the latest list
    const accountsCol2 = collection(db, 'users', currentUser.uid, 'accounts');
    const snap = await getDocs(accountsCol2);
    const allAccounts = snap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    const refreshedNewAccount = allAccounts.find(acc => acc.id === docRef.id);
    if (refreshedNewAccount) {
      setSelectedAccount(refreshedNewAccount);
      // Save selected account preference
      const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
      await setDoc(prefsRef, { selectedAccountId: refreshedNewAccount.id }, { merge: true });
    }

    // Close the modal
    setShowAddAccountModal(false);

    // Trigger data refresh to update Summary and Trades pages
    setDataRefreshTrigger(prev => prev + 1);

  };

  // Save selected account preference whenever it changes
  useEffect(() => {
    if (!currentUser || !selectedAccount) return;
    
    async function saveSelectedAccount() {
      try {
        const { db } = await import('./firebase');
        const { doc, setDoc } = await import('firebase/firestore');
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        await setDoc(prefsRef, { selectedAccountId: selectedAccount.id }, { merge: true });
      } catch (error) {
        console.error('Error saving selected account preference:', error);
      }
    }
    
    saveSelectedAccount();
  }, [currentUser, selectedAccount]);

  const handleArchiveAccount = async () => {
    if (!accountToDelete || !currentUser) return;
    
    try {
      const { db } = await import('./firebase');
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'users', currentUser.uid, 'accounts', accountToDelete.id), {
        archived: true,
        archivedAt: new Date().toISOString(),
        archiveReason: accountToDelete.evaluationStatus === 'passed' ? 'evaluation_passed' : 'manual',
      });
      
      await refreshAccounts();
      setDataRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Error archiving account:', error);
      alert('Failed to archive account. Please try again.');
    } finally {
      setShowDeleteConfirm(false);
      setAccountToDelete(null);
      setDeleteConfirmText('');
    }
  };

  const markEvaluationPassed = async (accountId) => {
    if (!currentUser || !accountId) {
      throw new Error('User or account is missing');
    }

    const account = accounts.find((candidate) => candidate.id === accountId);
    if (!account || account.propPhase !== 'evaluation') {
      throw new Error('Only active evaluation accounts can be marked as passed');
    }

    const { db } = await import('./firebase');
    const { collection, doc, getDocs, updateDoc } = await import('firebase/firestore');
    const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', accountId, 'entries');
    const entriesSnap = await getDocs(entriesCol);
    const entries = entriesSnap.docs.map((entryDoc) => ({ id: entryDoc.id, ...entryDoc.data() }));
    const progress = getEvaluationProgress(account, entries);

    if (!progress.targetReached) {
      throw new Error(`Evaluation target has not been reached. $${progress.remaining.toFixed(2)} remains.`);
    }

    const now = new Date().toISOString();
    await updateDoc(doc(db, 'users', currentUser.uid, 'accounts', accountId), {
      evaluationStatus: 'passed',
      passedAt: now,
      passedProfit: progress.profit,
      archived: true,
      archivedAt: now,
      archiveReason: 'evaluation_passed',
    });

    await refreshAccounts();
    setDataRefreshTrigger(prev => prev + 1);
    return progress;
  };

  const restoreArchivedAccount = async (accountId) => {
    if (!currentUser || !accountId) return;
    const { db } = await import('./firebase');
    const { doc, updateDoc } = await import('firebase/firestore');
    await updateDoc(doc(db, 'users', currentUser.uid, 'accounts', accountId), {
      archived: false,
      restoredAt: new Date().toISOString(),
      archivedAt: null,
      archiveReason: null,
    });
    await refreshAccounts();
    setShowAddAccountModal(false);
    setDataRefreshTrigger(prev => prev + 1);
  };

  if (!authReady) return null;

  return (
    <UserContext.Provider value={{ 
      accountsLoaded,
      currentUser, 
      accounts, 
      archivedAccounts,
      selectedAccount, 
      setSelectedAccount, 
      setAccounts,
      refreshAccounts,
      markEvaluationPassed,
      restoreArchivedAccount,
      refreshEasterEggs,
      setShowDeleteConfirm,
      setAccountToDelete,
      dataRefreshTrigger,
      triggerDataRefresh: () => setDataRefreshTrigger(prev => prev + 1),
      showICTEmail: () => easterEggs.ictEmail && setShowICTEmail(true),
      showBandzEmail: () => easterEggs.bandzEmail && setShowBandzEmail(true),
      easterEggs,
      setEliteBadgeAchieved: async (userId) => {
        try {
          const { db } = await import('./firebase');
          const { doc, setDoc } = await import('firebase/firestore');
          const prefsRef = doc(db, 'users', userId, 'preferences', 'settings');
          await setDoc(prefsRef, { 
            hasAchieved10Streak: true,
            eliteBadgeDate: new Date().toISOString()
          }, { merge: true });
        } catch (error) {
          console.error('Error saving elite badge state:', error);
        }
      },
      setICTEmailFound: async (userId) => {
        try {
          const { db } = await import('./firebase');
          const { doc, setDoc } = await import('firebase/firestore');
          const prefsRef = doc(db, 'users', userId, 'preferences', 'settings');
          await setDoc(prefsRef, { 
            hasFoundICTEmail: true
          }, { merge: true });
        } catch (error) {
          console.error('Error saving ICT email found state:', error);
        }
      },
      setRocketShipFound: async (userId) => {
        try {
          const { db } = await import('./firebase');
          const { doc, setDoc } = await import('firebase/firestore');
          const prefsRef = doc(db, 'users', userId, 'preferences', 'settings');
          await setDoc(prefsRef, { 
            hasFoundRocketShip: true
          }, { merge: true });
        } catch (error) {
          console.error('Error saving rocket ship found state:', error);
        }
      }
    }}>
      <ThemeProvider>
        <LanguageProvider currentUser={currentUser}>
        <ToastProvider>
        <ConfirmProvider>
          <div className={currentUser ? "authenticated-app min-h-screen theme-app-shell" : undefined}>
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <RouteTitle signedIn={Boolean(currentUser)} />
            {currentUser ? (
              <>
                <WeeklyReviewAutomation
                  currentUser={currentUser}
                  accounts={accounts}
                  onSelectAccount={setSelectedAccount}
                  dataRefreshTrigger={dataRefreshTrigger}
                />
                <Layout>
                  <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/month/:year/:month" element={<MonthPage />} />
                    <Route path="/day/:year/:month/:day" element={<DayPage />} />
                    <Route path="/day/:month/:day" element={<DayPage />} />
                    <Route path="/trades" element={<TradesPage />} />
                    <Route path="/context" element={<ContextPage />} />
                    <Route path="/visualize" element={<VisualizationPage />} />
                    <Route path="/edit-trade/:entryId" element={<EditTradePage />} />
                    <Route path="/notebook" element={<NotebookPage />} />
                    <Route path="/notebook/graph" element={<NotebookGraphPage />} />
                    <Route path="/summary" element={<SummaryPage />} />
                    <Route path="/edit-account" element={<EditAccountPage />} />
                    <Route path="/settings" element={<SettingsPage />} />
                    <Route path="/themes" element={<ThemeStudioPage />} />
                    <Route path="/admin" element={<AdminPage />} />
                    <Route path="/test-data" element={<TestDataPage />} />
                    <Route path="/account-activity" element={<AccountActivityPage />} />
                    <Route
                      path="/deposit"
                      element={<Navigate to="/account-activity" replace state={{ openForm: true, activityType: "deposit" }} />}
                    />
                    <Route
                      path="/payout"
                      element={<Navigate to="/account-activity" replace state={{ openForm: true, activityType: "payout" }} />}
                    />
                    <Route path="/feedback" element={<FeedbackPage />} />
                    <Route path="/socials" element={<SocialsPage />} />
                    <Route path="/terminal" element={<TerminalPage />} />
                    <Route path="/bandzbot" element={<BandzBotPage />} />
                    <Route path="/checklist" element={<ChecklistPage />} />
                    <Route path="/quick-add" element={<QuickAddPage />} />
                    <Route path="/import-trades" element={<CsvImportPage />} />
                    <Route path="/connections" element={<BrokerConnectionsPage />} />
                    <Route path="/connections/webull/callback" element={<WebullCallbackPage />} />
                    {process.env.NODE_ENV === 'development' && (
                      <Route path="/feature-preview" element={<FeaturePreviewPage />} />
                    )}
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                  </Suspense>
                </Layout>
                
                {/* Add Account Modal - shown automatically for new users or when manually triggered */}
                <AddAccountModal
                  isOpen={showAddAccountModal}
                  onClose={() => {
                    // Archived-only users still need access to Settings so they can restore an account.
                    if (accounts.length > 0 || archivedAccounts.length > 0) {
                      setShowAddAccountModal(false);
                    }
                  }}
                  onSave={handleCreateAccount}
                  currentUser={currentUser}
                  required={accounts.length === 0 && archivedAccounts.length === 0}
                />
                <LanguageSetupModal />
              </>
            ) : (
              <Suspense fallback={<PageLoader />}>
              <Routes>
                {process.env.NODE_ENV === 'development' && (
                  <>
                    <Route path="/settings-preview" element={<SettingsPage previewMode />} />
                    <Route path="/feature-preview" element={<FeaturePreviewPage />} />
                    <Route path="/csv-import-preview" element={<CsvImportPage previewMode />} />
                    <Route path="/connections-preview" element={<BrokerConnectionsPage previewMode />} />
                    <Route path="/context-preview" element={<Layout><ContextPage previewMode /></Layout>} />
                    <Route path="/trades-preview" element={<Layout><TradesPage previewMode /></Layout>} />
                    <Route path="/language-setup-preview" element={<LanguageSetupModal forceOpen />} />
                  </>
                )}
                <Route path="*" element={<LandingPage />} />
              </Routes>
              </Suspense>
            )}
          </Router>

          <OfflineBanner signedIn={Boolean(currentUser)} />
          
          {/* Archive Account Confirmation Dialog */}
          {showDeleteConfirm && accountToDelete && (
            <div className="fixed inset-0 bg-black/50 dark:bg-black/70 backdrop-blur-sm flex items-center justify-center z-[70] p-4">
              <div className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card p-6 max-w-md w-full shadow-elevated">
                <h3 className="text-xl font-semibold text-[#111827] dark:text-[#F9FAFB] mb-4">Archive Account</h3>
                <p className="text-[#6B7280] dark:text-[#9CA3AF] mb-6">
                  Archive <span className="text-[#8B5CF6] dark:text-[#A78BFA] font-semibold">"{accountToDelete.name}"</span>?
                  It will leave the active account list, while its trades remain in all-account metrics. You can restore it from Settings.
                </p>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#374151] dark:text-[#D1D5DB] mb-2">
                    Type "ARCHIVE" to confirm:
                  </label>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && deleteConfirmText.trim() === "ARCHIVE") {
                        handleArchiveAccount();
                      }
                    }}
                    className="w-full bg-white dark:bg-[#1F2937] text-[#111827] dark:text-[#F9FAFB] p-3 rounded-card border border-[#E5E7EB] dark:border-[#374151] focus:ring-2 focus:ring-[#8B5CF6] focus:border-[#8B5CF6] focus:outline-none transition-all"
                    placeholder="ARCHIVE"
                    autoFocus
                  />
                </div>
                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => {
                      setShowDeleteConfirm(false);
                      setAccountToDelete(null);
                      setDeleteConfirmText('');
                    }}
                    className="px-4 py-2 bg-[#F3F4F6] dark:bg-[#374151] hover:bg-[#E5E7EB] dark:hover:bg-[#4B5563] text-[#111827] dark:text-[#F9FAFB] rounded-card transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleArchiveAccount}
                    disabled={deleteConfirmText.trim() !== "ARCHIVE"}
                    className={`px-4 py-2 rounded-card transition-colors ${
                      deleteConfirmText.trim() === "ARCHIVE"
                        ? "bg-[#8B5CF6] hover:bg-[#7C3AED] text-white cursor-pointer"
                        : "bg-[#F3F4F6] dark:bg-[#374151] text-[#9CA3AF] dark:text-[#6B7280] cursor-not-allowed opacity-50 pointer-events-none"
                    }`}
                  >
                    Archive Account
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ICT Email Toast */}
          <ICTEmailToast 
            show={showICTEmail} 
            onClose={() => setShowICTEmail(false)} 
          />

          {/* Bandz Elite Email Toast */}
          <BandzEliteToast 
            show={showBandzEmail} 
            onClose={() => setShowBandzEmail(false)} 
          />
          </div>
        </ConfirmProvider>
        </ToastProvider>
        </LanguageProvider>
      </ThemeProvider>
    </UserContext.Provider>
  );
}

export default App;
